// Client side of Phase 4: scoped, limited, live watchers over handovers, their units, partner
// updates, supplier bills and partner scorecards, plus the callable that reads a partner's
// message or file (12, 14). All of these documents are written by the server; clients change them
// only through commands (04 §4.5b, §4.6). Every query has a `where` on scope (branch or sector, or
// the parent document of a subcollection) and a `limit` (02 §2.3). The composite indexes each
// watcher needs are listed in its comment.
import {
  collection,
  doc,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import { ulid } from 'ulid';
import { formatPrefixedId } from '@ecobox/domain';
import type {
  ImportMapping,
  ImportPartnerUpdatesInput,
  ImportPartnerUpdatesOutput,
  PartnerScorecardDoc,
  billDocSchema,
  handoverDocSchema,
  handoverItemDocSchema,
  partnerUpdateDocSchema,
} from '@ecobox/schema';
import { toConfigError } from './config';
import { toDoc, watchOne, watchRows, type Unsubscribe } from './docs';
import type { BoxRow, ShipmentRow } from './counter';
import type { NotificationRow } from './money';
import { watchBoxesHere, type MovementScope } from './movement';

export type HandoverRow = (typeof handoverDocSchema)['_output'];
export type HandoverItemRow = (typeof handoverItemDocSchema)['_output'];
export type PartnerUpdateRow = (typeof partnerUpdateDocSchema)['_output'];
export type BillRow = (typeof billDocSchema)['_output'];

export const HANDOVER_LIST_LIMIT = 50;
/** A handover carries a few hundred units (one container); the most one screen loads. */
export const HANDOVER_ITEMS_LIMIT = 1000;
/** The most handovers whose units one screen watches at once (the console's open units). */
export const MAX_WATCHED_HANDOVERS = 40;
export const PARTNER_UPDATE_LIMIT = 200;
export const BILL_LIST_LIMIT = 50;
/** Handover statuses where units are with the partner: the updates console works on these. */
export const ACTIVE_HANDOVER_STATUSES = ['handed_over', 'in_progress'] as const;
/** Handover statuses where staff are still putting units on: not signed yet. */
export const OPEN_HANDOVER_STATUSES = ['draft', 'loading'] as const;
/** Shipment statuses whose boxes can still be at the hub waiting for a partner. */
export const WAITING_SHIPMENT_STATUSES = [
  'booked',
  'at_origin',
  'in_transit',
  'arrived',
  'partially_delivered',
] as const;
export const SHIPMENTS_PER_LOOKUP = 30;

const scopeFilter = (scope: MovementScope): QueryConstraint =>
  'branchId' in scope
    ? where('branchId', '==', scope.branchId)
    : where('sectorId', '==', scope.sectorId);

const row =
  <T>(idField: string) =>
  (id: string, data: DocumentData): T =>
    toDoc<T>(id, data, idField);

// ---- new ids -------------------------------------------------------------------

/** A new `hnd_…` id made on the device, so the handover can be named before the server has it. */
export function newHandoverId(): string {
  return formatPrefixedId('hnd', ulid());
}

/** A new `bil_…` id made on the device. */
export function newBillId(): string {
  return formatPrefixedId('bil', ulid());
}

// ---- handovers ------------------------------------------------------------------

/**
 * Handovers of a branch or country, newest first (or, with `statuses`, only those, unordered).
 * `partnerId` narrows to one partner. Indexes: `handovers (branchId | sectorId asc, createdAt desc)`
 * and `handovers (branchId | sectorId asc, partnerId asc, createdAt desc)`. With `statuses` there is
 * no ordering (no composite index): the screen sorts what comes back.
 */
export function watchHandovers(
  db: Firestore,
  args: { scope: MovementScope; partnerId?: string; statuses?: readonly string[]; max?: number },
  onData: (rows: HandoverRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const constraints: QueryConstraint[] = [scopeFilter(args.scope)];
  if (args.partnerId) constraints.push(where('partnerId', '==', args.partnerId));
  if (args.statuses && args.statuses.length > 0) {
    constraints.push(where('status', 'in', [...args.statuses]));
  } else {
    constraints.push(orderBy('createdAt', 'desc'));
  }
  constraints.push(limit(args.max ?? HANDOVER_LIST_LIMIT));
  return watchRows(
    query(collection(db, 'handovers'), ...constraints),
    row<HandoverRow>('id'),
    onData,
    onError,
  );
}

/** One handover, live. */
export function watchHandover(
  db: Firestore,
  handoverId: string,
  onData: (row: HandoverRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'handovers', handoverId), row<HandoverRow>('id'), onData, onError);
}

/** The units of one handover. A subcollection is scoped by its parent, which the caller read. At most 1000. */
export function watchHandoverItems(
  db: Firestore,
  args: { handoverId: string; max?: number },
  onData: (rows: HandoverItemRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'handovers', args.handoverId, 'items'),
    limit(Math.min(args.max ?? HANDOVER_ITEMS_LIMIT, HANDOVER_ITEMS_LIMIT)),
  );
  return watchRows(q, row<HandoverItemRow>('unitId'), onData, onError);
}

/**
 * The units of several handovers at once (the console's "open units"): one listener per handover,
 * at most 40 handovers. Gives the latest rows of every handover, keyed by handover id, each time
 * one of them changes; nothing is given until every handover has answered once.
 */
export function watchItemsOfHandovers(
  db: Firestore,
  handoverIds: readonly string[],
  onData: (byHandover: ReadonlyMap<string, HandoverItemRow[]>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ids = [...new Set(handoverIds)].slice(0, MAX_WATCHED_HANDOVERS);
  const latest = new Map<string, HandoverItemRow[]>();
  if (ids.length === 0) {
    onData(latest);
    return () => undefined;
  }
  const stops = ids.map((handoverId) =>
    watchHandoverItems(
      db,
      { handoverId },
      (rows) => {
        latest.set(handoverId, rows);
        if (latest.size === ids.length) onData(new Map(latest));
      },
      onError,
    ),
  );
  return () => stops.forEach((stop) => stop());
}

// ---- partner updates -------------------------------------------------------------

/**
 * Updates as received, for audit and POD. With `handoverId`: that handover's updates (equality
 * filters only, at most 200; the screen sorts). Without: the newest first, 50 by default. Index:
 * `partnerUpdates (branchId | sectorId asc, createdAt desc)`.
 */
export function watchPartnerUpdates(
  db: Firestore,
  args: { scope: MovementScope; handoverId?: string; max?: number },
  onData: (rows: PartnerUpdateRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const constraints: QueryConstraint[] = [scopeFilter(args.scope)];
  if (args.handoverId) {
    constraints.push(
      where('handoverId', '==', args.handoverId),
      limit(Math.min(args.max ?? PARTNER_UPDATE_LIMIT, PARTNER_UPDATE_LIMIT)),
    );
  } else {
    constraints.push(orderBy('createdAt', 'desc'), limit(args.max ?? 50));
  }
  return watchRows(
    query(collection(db, 'partnerUpdates'), ...constraints),
    row<PartnerUpdateRow>('id'),
    onData,
    onError,
  );
}

// ---- bills -----------------------------------------------------------------------

/** Bill statuses that still need something from a person: approval, a payment or both. */
export const OPEN_BILL_STATUSES = [
  'recorded',
  'awaiting_approval',
  'approved',
  'part_paid',
] as const;

/**
 * Supplier bills of a branch or country, the soonest due first. With `statuses`, only those. With
 * `partnerId`, that partner's bills, newest bill first. Indexes: `bills (branchId | sectorId asc,
 * dueDate asc)`, `bills (branchId | sectorId asc, status asc, dueDate asc)` and `bills (partnerId asc,
 * billDate desc)`.
 */
export function watchBills(
  db: Firestore,
  args: { scope: MovementScope; partnerId?: string; statuses?: readonly string[]; max?: number },
  onData: (rows: BillRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const constraints: QueryConstraint[] = [scopeFilter(args.scope)];
  if (args.partnerId) {
    constraints.push(where('partnerId', '==', args.partnerId), orderBy('billDate', 'desc'));
  } else {
    if (args.statuses && args.statuses.length > 0) {
      constraints.push(where('status', 'in', [...args.statuses]));
    }
    constraints.push(orderBy('dueDate', 'asc'));
  }
  constraints.push(limit(args.max ?? BILL_LIST_LIMIT));
  return watchRows(
    query(collection(db, 'bills'), ...constraints),
    row<BillRow>('id'),
    onData,
    onError,
  );
}

/** One bill, live. */
export function watchBill(
  db: Firestore,
  billId: string,
  onData: (row: BillRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'bills', billId), row<BillRow>('id'), onData, onError);
}

// ---- scorecards ------------------------------------------------------------------

/** `partnerScorecards/{partnerId}` (12 §12.4): rolling 90 days, computed daily by the control tower. */
export type PartnerScorecardRow = PartnerScorecardDoc;

/** One partner's scorecard, live. `null` until the server has made one. Read by id: no query. */
export function watchPartnerScorecard(
  db: Firestore,
  partnerId: string,
  onData: (row: PartnerScorecardRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(
    doc(db, 'partnerScorecards', partnerId),
    (id, data) => toDoc<PartnerScorecardRow>(id, data, 'partnerId'),
    onData,
    onError,
  );
}

export const MAX_WATCHED_SCORECARDS = 60;

/**
 * The scorecards of the partners the caller names (from the partner list), one document each, at
 * most 60. Gives everything known so far each time one changes.
 */
export function watchPartnerScorecards(
  db: Firestore,
  partnerIds: readonly string[],
  onData: (rows: ReadonlyMap<string, PartnerScorecardRow>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ids = [...new Set(partnerIds)].slice(0, MAX_WATCHED_SCORECARDS);
  const latest = new Map<string, PartnerScorecardRow>();
  const answered = new Set<string>();
  if (ids.length === 0) {
    onData(latest);
    return () => undefined;
  }
  const stops = ids.map((id) =>
    watchPartnerScorecard(
      db,
      id,
      (card) => {
        answered.add(id);
        if (card) latest.set(id, card);
        else latest.delete(id);
        if (answered.size === ids.length) onData(new Map(latest));
      },
      onError,
    ),
  );
  return () => stops.forEach((stop) => stop());
}

// ---- what is waiting for a partner -----------------------------------------------------

/**
 * Shipments whose coverage says a partner delivers them from this hub, and that can still have
 * boxes waiting there. Equality filters plus an `in` filter: no composite index. At most 500.
 * ASSUMPTION for the rules: staff at the serving hub may read shipments whose
 * `destination.servingBranchId` is their branch (a hub's own scope does not cover the origin
 * country's shipments).
 */
export function watchPartnerShipmentsAtHub(
  db: Firestore,
  args: { hubId: string; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    where('destination.servingBranchId', '==', args.hubId),
    where('destination.mode', '==', 'partner'),
    where('status', 'in', [...WAITING_SHIPMENT_STATUSES]),
    limit(Math.min(args.max ?? 500, 500)),
  );
  return watchRows(q, (id, data) => toDoc<ShipmentRow>(id, data, 'trackingNo'), onData, onError);
}

/** What a hub has to hand to partners: its boxes, and the shipments they belong to. */
export interface WaitingSource {
  boxes: BoxRow[];
  shipments: ShipmentRow[];
}

/**
 * The boxes waiting at a branch and the partner-mode shipments of that hub, together: the
 * Handovers screen joins them with the coverage map. Nothing is given until both have answered.
 * Prime pieces at an origin branch come in the `boxes` list (their ids end in `-P1`, `-P2`, …).
 */
export function watchUnitsWaiting(
  db: Firestore,
  args: { branchId: string },
  onData: (source: WaitingSource) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  let boxes: BoxRow[] | undefined;
  let shipments: ShipmentRow[] | undefined;
  const emit = (): void => {
    if (boxes && shipments) onData({ boxes, shipments });
  };
  const stopBoxes = watchBoxesHere(
    db,
    { branchId: args.branchId },
    (rows) => {
      boxes = rows;
      emit();
    },
    onError,
  );
  const stopShipments = watchPartnerShipmentsAtHub(
    db,
    { hubId: args.branchId },
    (rows) => {
      shipments = rows;
      emit();
    },
    (err) => {
      // The boxes still show (with the destination the server put on them, if it did).
      shipments ??= [];
      emit();
      onError(err);
    },
  );
  return () => {
    stopBoxes();
    stopShipments();
  };
}

/**
 * The shipments behind a handover's units (consignee, address and notes for the manifest).
 * Looked up by tracking number in groups of 30, at most 10 groups (300 shipments), each group
 * scoped by the serving hub (`side: 'origin'`: by the branch that took the shipment in, for Prime). Gives everything found so far each time a group changes.
 */
export function watchShipmentsByTracking(
  db: Firestore,
  args: { hubId: string; trackingNos: readonly string[]; side?: 'serving' | 'origin' },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const unique = [...new Set(args.trackingNos)].slice(0, SHIPMENTS_PER_LOOKUP * 10);
  const groups: string[][] = [];
  for (let i = 0; i < unique.length; i += SHIPMENTS_PER_LOOKUP) {
    groups.push(unique.slice(i, i + SHIPMENTS_PER_LOOKUP));
  }
  if (groups.length === 0) {
    onData([]);
    return () => undefined;
  }
  const latest = groups.map(() => [] as ShipmentRow[]);
  const answered = new Set<number>();
  const stops = groups.map((trackingNos, i) =>
    watchRows(
      query(
        collection(db, 'shipments'),
        // Balikbayan boxes are read at the hub that serves the city; Prime pieces where they were taken in.
        args.side === 'origin'
          ? where('branchId', '==', args.hubId)
          : where('destination.servingBranchId', '==', args.hubId),
        where('trackingNo', 'in', trackingNos),
        limit(SHIPMENTS_PER_LOOKUP),
      ),
      (id, data) => toDoc<ShipmentRow>(id, data, 'trackingNo'),
      (rows) => {
        latest[i] = rows;
        answered.add(i);
        if (answered.size === groups.length) onData(latest.flat());
      },
      onError,
    ),
  );
  return () => stops.forEach((stop) => stop());
}

/**
 * A branch's newest Prime shipments (documents and urgent parcels for couriers). Index:
 * `shipments (branchId asc, service asc, createdAt desc)`.
 */
export function watchPrimeShipments(
  db: Firestore,
  args: { branchId: string; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    where('branchId', '==', args.branchId),
    where('service', '==', 'prime'),
    orderBy('createdAt', 'desc'),
    limit(args.max ?? 50),
  );
  return watchRows(q, (id, data) => toDoc<ShipmentRow>(id, data, 'trackingNo'), onData, onError);
}

// ---- what the server sent about a handover ---------------------------------------------

/**
 * The WhatsApp messages the server queued for one handover (the manifest, chases): the outbox rows
 * that carry its id, newest first, 20 at most. Scoped by the branch the handover belongs to.
 * Index: `notificationOutbox (handoverId asc, createdAt desc)`.
 */
export function watchHandoverMessages(
  db: Firestore,
  args: { branchId: string; handoverId: string },
  onData: (rows: NotificationRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'notificationOutbox'),
    where('branchId', '==', args.branchId),
    where('handoverId', '==', args.handoverId),
    orderBy('createdAt', 'desc'),
    limit(20),
  );
  return watchRows(q, row<NotificationRow>('id'), onData, onError);
}

// ---- importPartnerUpdates (callable) ----------------------------------------------------

export type { ImportMapping, ImportPartnerUpdatesInput, ImportPartnerUpdatesOutput };
export const IMPORT_MAPPING_FIELDS = [
  'unit',
  'trackingNo',
  'ref',
  'status',
  'at',
  'receiver',
  'note',
  'collected',
] as const satisfies readonly (keyof ImportMapping)[];

/**
 * Reads a partner's WhatsApp message or file and proposes updates (12 §12.9). Writes nothing but the
 * remembered column map (`partnerImportMaps/{partnerId}`). Online only. Send each confirmed
 * proposal as a `partnerUpdate` command with `source: 'import'`.
 */
export async function importPartnerUpdates(
  functions: Functions,
  input: ImportPartnerUpdatesInput,
): Promise<ImportPartnerUpdatesOutput> {
  try {
    const result = await httpsCallable<ImportPartnerUpdatesInput, ImportPartnerUpdatesOutput>(
      functions,
      'importPartnerUpdates',
    )(input);
    const data = result.data;
    return {
      ...data,
      proposals: data.proposals ?? [],
      unmatched: data.unmatched ?? [],
      mapping: data.mapping ?? null,
      hasHeader: data.hasHeader ?? null,
    };
  } catch (err) {
    throw toConfigError(err);
  }
}
